/**
 * Organization model interfaces matching the Organization API specification
 * 
 * @remarks
 * These models correspond to the Organization API endpoints at `/api/organizations`
 * All interfaces are designed to match the API response structure exactly.
 * 
 * @see ORGANIZATION_API_REFERENCE.md for complete API documentation
 */

import { BaseEntity, UserRef, OrganizationMember, OrganizationMemberPopulated } from './base';
import { OrgSubscription } from './subscription.model';

/**
 * Core Organization model matching API schema
 * 
 * @remarks
 * This model represents an organization with non-populated references.
 * For populated version with expanded user details, use OrganizationPopulated.
 */
export interface Organization extends BaseEntity {
  /**
   * Unique organization code
   * Auto-generated if not provided during creation
   */
  orgCode: string;

  /**
   * Organization name (2-100 characters)
   * Must be unique per user
   */
  name: string;

  /**
   * ID of the user who created the organization
   */
  createdBy: string;

  /**
   * Array of organization members with their roles
   */
  members: OrganizationMember[];

  /**
   * Subscription plan associated with this organization.
   * Populated by the backend after plan assignment.
   * Absent for organizations created before subscription support was introduced.
   */
  subscription?: OrgSubscription;
}

/**
 * Organization with populated related entities
 * 
 * @remarks
 * Returned by GET /api/organizations/:id and single organization endpoints
 * Contains expanded user details for creator and members
 */
export interface OrganizationPopulated extends Omit<Organization, 'createdBy' | 'members'> {
  /**
   * Full creator user reference with details
   */
  createdBy: UserRef;

  /**
   * Array of members with populated user details
   */
  members: OrganizationMemberPopulated[];
}

/**
 * Data Transfer Object for creating a new organization
 * 
 * @remarks
 * Used in POST /api/organizations
 * The authenticated user automatically becomes the creator and first Admin member.
 */
export interface CreateOrganizationDto {
  /**
   * Organization name (required, 2-100 characters)
   * Must be unique for the authenticated user
   */
  name: string;

  /**
   * Optional organization code
   * If not provided, will be auto-generated
   * Must be unique across all organizations
   */
  orgCode?: string;
}

/**
 * Data Transfer Object for updating an organization
 * 
 * @remarks
 * Used in PUT /api/organizations/:id
 * Only organization members can update.
 * All fields are optional - only provide fields to update.
 */
export interface UpdateOrganizationDto {
  /**
   * Updated organization name (2-100 characters)
   * Must remain unique for the user
   */
  name?: string;

  /**
   * Updated organization code
   * Must remain unique across all organizations
   */
  orgCode?: string;
}

/**
 * Query parameters for organization search and filtering
 * 
 * @remarks
 * Used in GET /api/organizations
 */
export interface OrganizationQueryParams {
  /**
   * Filter to show only organizations created by the authenticated user
   * When omitted, shows organizations where user is a member
   */
  createdByMe?: boolean;

  /**
   * Additional dynamic parameters
   */
  [key: string]: string | number | boolean | undefined;
}

/**
 * API response wrapper for single organization
 */
export interface OrganizationResponse {
  success: boolean;
  data: OrganizationPopulated;
  message?: string;
}

/**
 * API response wrapper for organization list
 */
export interface OrganizationListResponse {
  success: boolean;
  data: OrganizationPopulated[];
  count?: number;
  message?: string;
}

/**
 * API response wrapper for organization creation
 */
export interface CreateOrganizationResponse {
  success: boolean;
  data: OrganizationPopulated;
  message: 'Organization created successfully';
}

/**
 * API response wrapper for organization update
 */
export interface UpdateOrganizationResponse {
  success: boolean;
  data: OrganizationPopulated;
  message: 'Organization updated successfully';
}

/**
 * API response wrapper for organization deletion
 */
export interface DeleteOrganizationResponse {
  success: boolean;
  message: 'Organization deleted successfully';
}
